import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { notification as antdNotification } from "antd";
import isEmpty from "../validation/is-empty";
import moment from "moment-timezone";

import P from "../assets/bookingeventassets/assets/p.svg";
import ZoomIcon from "../assets/icons/zoom.svg";
import MeetIcon from "../assets/icons/google-meet.svg";
import TeamsIcon from "../assets/icons/Microsoft_Teams.svg";
import GoToIcon from "../assets/icons/goto_icon.svg";
import PhoneCall from "../assets/icons/phone_call.svg";
import InPersonMeet from "../assets/icons/in_person_meet.svg";
import OtherMeet from "../assets/icons/other_meet_icon.svg";

/**
 * update filter field in component state
 * @param {*} name
 * @param {*} value
 * @param {*} searchFilters
 */
export const onUpdateFilters = (name, value, searchFilters) => {
  if (isEmpty(value)) {
    delete searchFilters[name];
  } else {
    searchFilters[name] = value;
  }
  return searchFilters;
};

/**
 * used for get query params
 * @returns
 */
export const useQuery = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

/**
 * with router high order component
 * @param {*} Component
 * @returns
 */
export const withRouter = (Component) => {
  const ComponentWithRouterProp = (props) => {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();
    return <Component {...props} router={{ location, navigate, params }} />;
  };
  return ComponentWithRouterProp;
};

/**
 * convert object to form data
 * @param {*} obj
 * @param {*} form
 * @param {*} namespace
 * @returns
 */
export const objectToFormData = (obj, form, namespace) => {
  var fd = form || new FormData();
  var formKey;
  for (var property in obj) {
    if (obj.hasOwnProperty(property)) {
      if (namespace) {
        formKey = namespace + "[" + property + "]";
      } else {
        formKey = property;
      }
      // if the property is an object, but not a File,
      // use recursivity.
      if (
        typeof obj[property] === "object" &&
        !(obj[property] instanceof File)
      ) {
        objectToFormData(obj[property], fd, formKey);
      } else {
        // if it's a string or a File object
        fd.append(formKey, obj[property]);
      }
    }
  }
  return fd;
};

/**
 * Convert hour
 * @param string hours
 * @returns
 */
export const convertHoursToMinutes = (hours) => {
  const timeParts = hours.split(":");
  const timeInMinutes = timeParts[0] * 60 + timeParts[1];
  return parseInt(timeInMinutes);
};

/**
 * Get day id
 * @param mixed weekDayId
 * @return int
 */
export const getDayId = (weekDayId) => {
  return weekDayId == "0" ? 7 : parseInt(weekDayId);
};

/**
 *
 * @param Date dated
 * @returns
 */
export const getFormatedDate = (dated) => {
  return (
    ("0" + dated.getDate()).slice(-2) +
    "-" +
    ("0" + (dated.getMonth() + 1)).slice(-2) +
    "-" +
    dated.getFullYear()
  );
};

/**
 *
 * @param Date dated
 * @returns
 */
export const getDBFormatedDate = (dated) => {
  if (!dated) return "";
  const d = dated instanceof Date ? dated : new Date(dated);
  if (Number.isNaN(d.getTime())) return "";
  return (
    d.getFullYear() +
    "-" +
    ("0" + (d.getMonth() + 1)).slice(-2) +
    "-" +
    ("0" + d.getDate()).slice(-2)
  );
};

/**
 * @param string htmlString
 * @returns string
 */
export const stripHTMLTags = (htmlString) => {
  return !isEmpty(htmlString) ? htmlString.replace(/<[^>]+>/g, "") : htmlString;
};

/**
 * Resolve a policy field value to a clickable URL, or null if not a valid link.
 */
export const resolvePolicyLinkUrl = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const trimmed = stripHTMLTags(value).trim();
  if (isEmpty(trimmed)) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      return new URL(trimmed).href;
    } catch {
      return null;
    }
  }

  const domainPattern =
    /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+(\/[^\s]*)?$/i;
  if (domainPattern.test(trimmed)) {
    return `https://${trimmed}`;
  }

  return null;
};

/**
 * Ensure a URL ends with a single trailing slash (CRA base URLs are concatenated).
 * @param {string} url
 * @returns {string}
 */
const ensureTrailingSlash = (url) => {
  if (!url || typeof url !== "string") {
    return "";
  }
  const trimmed = url.trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.endsWith("/") ? trimmed : `${trimmed}/`;
};

/**
 * Derive site origin from an API base like
 * https://host/backend/public/api/ → https://host/
 * @param {string} apiBase
 * @returns {string}
 */
const frontendFromApiBase = (apiBase) => {
  if (!apiBase) {
    return "";
  }
  return ensureTrailingSlash(
    apiBase.replace(/\/?backend\/public\/api\/?$/i, "/")
  );
};

/**
 * Frontend site origin (shares, company pages, image prefixes).
 * Prefer REACT_APP_FRONTEND_BASE_URL; else derive from REACT_APP_API_BASE_URL.
 * No staging/production host is hard-coded in source.
 */
export const FRONTEND_BASE_URL = ensureTrailingSlash(
  process.env.REACT_APP_FRONTEND_BASE_URL ||
    frontendFromApiBase(process.env.REACT_APP_API_BASE_URL) ||
    (typeof window !== "undefined" ? `${window.location.origin}/` : "/")
);

/**
 * Backend API base URL used by axios and OAuth / PayPal absolute links.
 * Prefer REACT_APP_API_BASE_URL; else FRONTEND_BASE_URL + backend/public/api/
 */
export const BACKEND_API_BASE_URL = ensureTrailingSlash(
  process.env.REACT_APP_API_BASE_URL ||
    `${FRONTEND_BASE_URL}backend/public/api/`
);



/**
 * Company base url
 */
export const COMPANY_BASE_URL = FRONTEND_BASE_URL+"company/";

/**
 * Company logo base url
 */
export const COMPANY_LOGO_BASE_URL = FRONTEND_BASE_URL+"backend/public/uploads/company-logos/";

/**
 * images base url
 */
export const IMAGE_BASE_URL = FRONTEND_BASE_URL + "backend/public/";

/**
 * show alert notification
 */
export const notification = {
  success: (description, message = "Success", placement = "topRight") => {
    antdNotification["success"]({
      message,
      description,
      placement,
    });
  },
  warning: (description, message = "Warning", placement = "topRight") => {
    antdNotification["warning"]({
      message,
      description,
      placement,
      duration: 6,
    });
  },
  error: (description, message = "Error", placement = "topRight") => {
    antdNotification["error"]({
      message,
      description,
      placement,
      duration: 10,
    });
  },
  info: (description, message = "Info", placement = "topRight") => {
    antdNotification["info"]({
      message,
      description,
      placement,
    });
  },
  upgrade: (description) => {
    antdNotification["warning"]({
      message: "Upgrade required",
      description,
      placement: "topRight",
      duration: 6,
    });
  },
};

/**
 * Inject preview-mode into embed widget data-config for admin preview tabs.
 */
const injectEmbedPreviewMode = (embedHtml) => {
  return embedHtml.replace(/data-config='([^']+)'/, (match, jsonStr) => {
    try {
      const config = JSON.parse(jsonStr);
      config["preview-mode"] = true;
      return `data-config='${JSON.stringify(config)}'`;
    } catch (error) {
      return match;
    }
  });
};

/**
 * Open embed code preview in a new tab with the chosen colors applied.
 * Uses synchronous document.write so pop-up blockers do not strip unsaved color config.
 */
export const openEmbedCodePreview = (embedHtml) => {
  if (isEmpty(embedHtml)) {
    notification.error("No embed code to preview.");
    return false;
  }

  const previewWindow = window.open("", "_blank");
  if (!previewWindow) {
    notification.warning("Please allow pop-ups to preview embed code.");
    return false;
  }

  const previewEmbedHtml = injectEmbedPreviewMode(embedHtml);
  const favicon32 = `${FRONTEND_BASE_URL}cropped-favicon-32x32.png`;
  const favicon192 = `${FRONTEND_BASE_URL}cropped-favicon-192x192.png`;

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>MeetOcto — Embed Preview</title>
  <link rel="icon" href="${favicon32}" sizes="32x32" />
  <link rel="icon" href="${favicon192}" sizes="192x192" />
  <style>
    body { margin: 0; padding: 16px; }
    #meetocto-booking-widget { min-height: 400px; width: 100%; }
  </style>
</head>
<body>
  ${previewEmbedHtml}
</body>
</html>`;

  previewWindow.document.open();
  previewWindow.document.write(html);
  previewWindow.document.close();
  return true;
};

/**
 * Open the public OctoLink company page in a new tab.
 */
export const openOctoLinkPreview = (companySlug) => {
  if (isEmpty(companySlug)) {
    notification.error("Your Octo Link is not set up yet.");
    return false;
  }

  window.open(`${COMPANY_BASE_URL}${companySlug}`, "_blank", "noopener,noreferrer");
  return true;
};

/**
 * Open the public event booking page in a new tab.
 */
export const openEventBookingPreview = (eventUrl, businessId = "") => {
  if (isEmpty(eventUrl)) {
    notification.error("Event link is not available.");
    return false;
  }

  const bookingPath = !isEmpty(businessId)
    ? `booking/${businessId}/${eventUrl}`
    : `booking/${eventUrl}`;

  window.open(`${FRONTEND_BASE_URL}${bookingPath}`, "_blank", "noopener,noreferrer");
  return true;
};

/**
 * Get Venue icon
 * @param {*} venue
 * @returns
 */
export const getVenueIcon = (venue) => {
  switch (venue) {
    case "Microsoft Teams":
      return <img src={TeamsIcon} alt="microsoft teams icon" />;
    case "Zoom":
      return <img src={ZoomIcon} alt="Zoom icon" />;
    case "Google Meet":
      return <img src={MeetIcon} alt="Google Meet icon" />;
    case "Phone Call":
      return <img src={PhoneCall} alt="Phone Call icon" />;
    case "In-Person Meeting":
      return <img src={InPersonMeet} alt="In Person Meet icon" />;
    case "Others":
      return <img src={OtherMeet} alt="Other meeting icon" />;
    case "Go to Meeting":
      return <img src={GoToIcon} alt="Go To Meeting icon" />;
    default:
      return null;
  }
};

/**
 * Capitalize first letter of word/string
 * @param {*} string
 * @returns
 */
export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * on modal open
 *
 */
export const onModalOpen = () => {
  document.getElementsByTagName("body")[0].classList.add("modal-open");
};

/**
 * on modal close
 */
export const onModalClose = () => {
  document.getElementsByTagName("body")[0].classList.remove("modal-open");
  setTimeout(() => {
    document.getElementsByTagName("body")[0].style.removeProperty("overflow");
  }, 500);
};

/**
 * Get user timezone
 */
export const getUserTimezoneId = () => {
  if (localStorage.userTimezoneId) {
    return localStorage.userTimezoneId;
  }
  return 60; // America/New_York
};

/**
 * Get Logged In user time zone
 * @returns string
 */
export const getLoggedInUserTimezone = () =>
  localStorage.userTimezone ? localStorage.userTimezone : "America/New_York";

export const emailRegex =
  /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

export const urlSlugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).+$/;




/**
 * check if property exist or not
 * @param {*} obj
 * @param {*} prop
 * @return boolean
 */
export const gotProperty = (obj, prop) => {
  return !isEmpty(obj) ? prop in obj : false;
};

/**
 * get user browser time zone
 * @returns string
 */
export const getUserBrowserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Compute booking end slot in the invitee-selected timezone.
 * @param {string} bookingDate - YYYY-MM-DD
 * @param {string} selectedSlot - HH:mm
 * @param {number|string} durationMinutes - event duration in minutes
 * @param {string} timezone - IANA timezone value
 * @returns {string} HH:mm
 */
export const computeBookingEndSlot = (
  bookingDate,
  selectedSlot,
  durationMinutes,
  timezone
) => {
  const tz = timezone || getUserBrowserTimezone();
  const bookingDateTime = `${bookingDate} ${selectedSlot}`;
  const duration = parseInt(durationMinutes, 10);
  const m = moment.tz(bookingDateTime, "YYYY-MM-DD HH:mm", tz);
  if (!isNaN(duration) && duration > 0) {
    return m.add(duration, "minutes").format("HH:mm");
  }
  return m.format("HH:mm");
};

/**
 * Convert from UtC time to user time
 * @param {*} utcTime
 * @param {*} timezone
 * @param {*} fromFormat
 * @param {*} toFormat
 */
export const toUserTime = (
  utcTime,
  timezone,
  fromFormat = "HH:mm:ss",
  toFormat = "HH:mm"
) => {
  return moment.tz(utcTime, fromFormat, "UTC").tz(timezone).format(toFormat);
};
